import * as Sentry from '@sentry/react';
import { matchQuery, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Auth, orgQueryKeys, TOTP, User, userQueryKeys, WebAuthn } from '#api';
import { AuthenticationError, NetworkError, trackError } from '#api/errors.ts';
import { deploymentFeatureFlagQueryOptions } from '#api/org.queries.ts';
import type { Organization } from '#dn-types/org.ts';
import { useAuthState } from '#hooks/useAuthState.ts';
import { clearMaxListReports } from '#util/api.ts';
import { commitLoginResultForSession } from './session-bound-login.ts';

const USER_TOKEN_EXPIRE_DAYS = 365 * 10; // 10 years, rely on redis
const AUTH_TOKEN_EXPIRE_DAYS = 0.67; // ~16 hours
const OIDC_TOKEN_EXPIRE_DAYS = 0.67; // ~16 hours

export type AuthUser =
  | {
      type: 'user';
      id: string;
      organizationID: string;
      email: string;
      hasTOTPAuthenticator: boolean;
      hasWebAuthnAuthenticator: boolean;
      organization: Organization;
    }
  | {
      type: 'oidcUser';
      organizationID: string;
      email: string;
      organization: Organization;
    };

/**
 * {@link useAuthProvider} hook creates auth object and handles state.
 * Any authentication APIs that change auth state should be called through
 * these methods, so that we can correctly track auth state and redirect as needed.
 */
export function useAuthProvider() {
  const { oidcState, oidcLogin, oidcRedirect, oidcFrom, userToken, authToken, oidcToken } = useAuthState();
  const queryClient = useQueryClient();

  // If we have a userToken or oidcToken, get user information
  // This suspense query is not wrapped by the normal suspense handling and <Authorized> handling as the rest of the app.
  const { data: user, refetch: refetchUser } = useSuspenseQuery({
    queryKey: userQueryKeys.current({ userToken: userToken.value, oidcToken: oidcToken.value }),
    queryFn: ({ signal }) =>
      userToken.value || oidcToken.value ?
        User.whoami({ signal }).catch((fetchErr) => {
          // We expect Authentication errors if the user is not logged in.
          if (fetchErr instanceof AuthenticationError) {
            // /v2/whoami allows partial auth, so no 2FA errors are expected here, only ERR_UNAUTHORIZED
            // Hopefully clearing auth or logging out will fix it
            if (fetchErr.code !== 'ERR_UNAUTHORIZED') {
              trackError(`Unexpected error code when checking user: ${fetchErr.code}`);
            }
            if (oidcToken.value) {
              // If the user had an oidcToken, we can use clearAuth to trigger a new oidc login
              // signout would also clear their `oidcRedirect`, which we don't want to do here.
              clearAuth();
            } else {
              // If they got an auth error even though they had a user token, those tokens are invalid.
              // Clear local state immediately instead of waiting on the logout endpoint.
              clearInvalidSession();
            }

            return null; // No user
          }
          // If it wasn't an authentication error, we need to throw an error for the global ErrorBoundary
          throw fetchErr;
        })
      : null,
    select(data) {
      // data will be null if there is no userToken or oidcToken
      if (!data) return null;

      switch (data.actorType) {
        case 'apiKey':
          throw Error("api key auth shouldn't be seen in webclient");
        case 'user': {
          const newUser = { ...data.actor, type: 'user', organization: data.organization } as const;
          Sentry.setUser(newUser);
          return newUser;
        }
        case 'oidcUser': {
          const newUser = { ...data.actor, type: 'oidcUser', organization: data.organization } as const;
          Sentry.setUser(newUser);
          return newUser;
        }
      }
    },
    retry: (failureCount) => {
      // Only retry once, so we can show an error screen quickly if needed.
      return failureCount < 1;
    },
    // Don't need to automatically refetch user info after we've gotten it. It won't change on its own
    staleTime: Number.POSITIVE_INFINITY,
  });

  const { data: deploymentFeatureFlags } = useQuery(deploymentFeatureFlagQueryOptions);
  useEffect(() => {
    if (deploymentFeatureFlags) {
      Sentry.setContext('deploymentFeatureFlags', { ...deploymentFeatureFlags });
    }
  }, [deploymentFeatureFlags]);

  useEffect(() => {
    Sentry.setUser(user);
  }, [user]);

  // Wipe the query cache when the user becomes unauthenticated. Doing this in an effect
  // (rather than synchronously inside _clearBrowserAuthState) ensures the cache wipe
  // happens *after* React has committed the unauthenticated render — by that point
  // <Protected> has rendered <Navigate> and the protected subtree has unmounted, so
  // queryClient.clear() doesn't re-suspend any mounted components or trigger refetches.
  // Without this ordering, the synchronous clear() un-suspended mounted queries during
  // the brief window before the redirect committed, producing a small post-logout 401
  // wave on top of the in-flight one. (We still need the cache wipe to prevent the next
  // signed-in user on the same browser session from reading cached data from the prior
  // user — query keys aren't scoped per-user.)
  const authState = getAuthState();
  useEffect(() => {
    if (authState === 'unauthenticated') {
      queryClient.clear();
      clearMaxListReports();
    }
  }, [authState, queryClient]);

  function sendMagicLink(email: string) {
    return Auth.sendMagicLink({ email });
  }

  function verifyMagicLink(magicLinkToken: string) {
    return Auth.verifyMagicLink({ magicLinkToken }).then(async (response) => {
      userToken.set(response.sessionToken, USER_TOKEN_EXPIRE_DAYS);
      // Await refetchUser so the user object is populated before callers
      // act on the resolved promise. Without this, there's a race where the
      // cookie change event hasn't fired yet and user is still null.
      return refetchUser();
    });
  }

  function getAuthState() {
    if (!userToken.value && !oidcToken.value) return 'unauthenticated';
    if (userToken.value && !authToken.value) return 'mfaRequired';
    return 'authenticated';
  }

  // We can use this function to determine whether to show the mfa login or mfa registration page
  function hasUserRegisteredMFA() {
    if (!user) return 'unknown';
    if (user.type === 'oidcUser') return 'n/a';
    if (user.hasTOTPAuthenticator || user.hasWebAuthnAuthenticator) return 'yes';
    return 'no';
  }

  function registerMFA(formData: Parameters<typeof TOTP.registerMFA>[0]) {
    if (!userToken.value) {
      // This should not happen, because this method is called from a protected route
      // which will redirect to the login screen if no user token is set.  But if it
      // does happen, we'll want to know about it.
      trackError(new Error('No user is set when calling registerMFA'));
    }
    return TOTP.registerMFA(formData).then((response) => {
      authToken.set(response.authToken, AUTH_TOKEN_EXPIRE_DAYS);
      return refetchUser();
    });
  }

  function registerWebAuthn(data: Parameters<typeof WebAuthn.finalizeRegistration>[0]) {
    if (!userToken.value) {
      trackError(new Error('No user is set when calling registerWebAuthn'));
      return Promise.reject(new Error('No user token'));
    }
    return WebAuthn.finalizeRegistration(data).then((response) => {
      authToken.set(response.authToken, AUTH_TOKEN_EXPIRE_DAYS);
      return refetchUser();
    });
  }

  async function loginWithWebAuthn(response: { sessionToken: string; authToken: string }) {
    userToken.set(response.sessionToken, USER_TOKEN_EXPIRE_DAYS);
    authToken.set(response.authToken, AUTH_TOKEN_EXPIRE_DAYS);
    // Clear out oidc state to prevent conflicts
    oidcRedirect.clear();
    oidcToken.clear();
    oidcState.clear();
    // The login page uses unauthenticated deployment-level flags. Now that the
    // user is authenticated, invalidate the customer-specific flags cache so the
    // app picks up org-level overrides. Same pattern as loginWithMFA and verifyOIDC.
    void queryClient.invalidateQueries({ queryKey: orgQueryKeys.featureFlags() });
    // Await refetchUser so the user object is populated before callers
    // act on the resolved promise. Without this, onSuccess fires before
    // user is available, causing a flash back to login.
    await refetchUser();
  }

  // Authenticate the user using a code from their 2FA app.
  // By the time this is used, the user should already have a valid user token.
  function loginWithMFA(code: string) {
    if (!userToken.value) {
      // This should not happen, because this method is called from a protected route
      // which will redirect to the login screen if no user token is set.  But if it
      // does happen, we'll want to know about it.
      trackError(new Error('No user is set when calling loginWithMFA'));
    }
    return commitLoginResultForSession({
      readSession: userToken.getCurrent,
      request: () => Auth.loginMFA({ code }),
      commit: (response) => {
        // Do not automatically redirect to OIDC login next time
        oidcRedirect.clear();
        // Clear out oidc state, to prevent conflicts
        oidcToken.clear();
        oidcState.clear();
        // Refresh feature flags to pick up customer-specific overrides
        void queryClient.invalidateQueries({ queryKey: orgQueryKeys.featureFlags() });
        return authToken.set(response.authToken, AUTH_TOKEN_EXPIRE_DAYS);
      },
    });
  }

  /**
   * This kicks off the OIDC flow, requesting some state and a redirect url from our server,
   * then saving off that state into browser storage and redirecting to the identity provider's login.
   */
  function startOIDC(orgId: string) {
    // Remove any pre-existing auth state in the browser, to avoid conflicts between TOTP and OIDC auth
    _clearBrowserAuthState();

    return Auth.startOIDCAuth(orgId).then((response) => {
      oidcState.set(response.oidcRequestToken);
      // This is used in LoginPage to redirect the user to the login url if their auth has expired.
      oidcLogin.set(window.location.pathname);
      // Technically the truthy check is unneeded, except in storybook where we do not want to actually redirect.
      if (response.redirectURL) {
        // Don't allow returning to our login page URL with the back button, would just cause another redirect.
        window.location.replace(response.redirectURL);
      }

      return;
    });
  }

  /**
   * Like {@link startOIDC}, but discovers the organization's OIDC provider from the email's
   * verified domain instead of an org-specific login URL. Used by the "Log in with SSO" button on
   * the login form. The org isn't known until verification completes, so the org-specific login
   * URL is stored by {@link verifyOIDC} instead of here.
   */
  function startOIDCByEmail(email: string) {
    // Remove any pre-existing auth state in the browser, to avoid conflicts between TOTP and OIDC auth
    _clearBrowserAuthState();

    return Auth.startOIDCAuthByEmail({ email }).then((response) => {
      oidcState.set(response.oidcRequestToken);
      // Technically the truthy check is unneeded, except in storybook where we do not want to actually redirect.
      if (response.redirectURL) {
        // Don't allow returning to our login page URL with the back button, would just cause another redirect.
        window.location.replace(response.redirectURL);
      }

      return;
    });
  }

  /**
   * Once the user hits our OIDC callback, we need to send the state code from the browser's storage, along with
   * the current url (including params that the identity provider added) to the server, in exchange for an
   * OIDC authentication+session token.
   */
  function verifyOIDC() {
    if (typeof oidcState.code !== 'string') {
      return Promise.reject(new Error('Could not find oidc state code in storage.'));
    }
    const location = window.location.href;
    return Auth.verifyOIDCAuth({ oidcRequestToken: oidcState.code, location }).then((response) => {
      oidcToken.set(response.authToken, OIDC_TOKEN_EXPIRE_DAYS);
      oidcRedirect.set('1');
      // Refresh feature flags to pick up customer-specific overrides
      void queryClient.invalidateQueries({ queryKey: orgQueryKeys.featureFlags() });

      // eslint-disable-next-line promise/no-nesting
      return refetchUser().then((result) => {
        // Remember the org-specific SSO login URL for the login-page button and expiry redirects.
        // startOIDC also stores it up front, but the email-discovery flow doesn't know the org
        // until now.
        const verifiedUser = result.data;
        if (verifiedUser?.type === 'oidcUser') {
          oidcLogin.set(`/auth/login/${verifiedUser.organizationID}`);
        }
        // Provide the oidcFrom so the caller can redirect
        return oidcFrom.url;
      });
    });
  }

  function signout() {
    return Auth.logout()
      .catch((err: unknown) => {
        if (err instanceof AuthenticationError || err instanceof NetworkError) {
          // No-op: the session is either already gone (mission accomplished), or the
          // request never left the machine. Reporting the latter here would capture an
          // envelope with the user and auth-bearing breadcrumbs attached (setUser(null)
          // has not run yet in `finally`). The capture itself precedes the clear, since
          // `.catch` runs before `.finally`; what lands afterwards is the IndexedDB `put`,
          // because the offline transport only queues once its tunnel fetch rejects. Either
          // way the exact envelope the clear exists to drop would be re-queued and flushed
          // at the next startup.
          //
          // This only covers _this_ logout's report. Any other capture whose tunnel POST is
          // still in flight when the clear commits can still land after it; narrow in
          // practice, since a genuinely offline fetch rejects immediately.
        } else {
          trackError(err);
        }
      })
      .finally(_clearBrowserAuthState);
  }

  /**
   * Clear all local authentication state after the API tells us the session token is invalid.
   *
   * This intentionally does not call the logout endpoint: the token cannot authenticate anymore,
   * and protected routes need to stop rendering immediately so they do not keep refetching.
   */
  function clearInvalidSession() {
    void queryClient.cancelQueries();
    queryClient.removeQueries({});
    _clearLocalAuthState();
  }

  function _clearLocalAuthState() {
    oidcRedirect.clear();
    oidcState.clear();
    userToken.clear();
    authToken.clear();
    oidcToken.clear();
    Sentry.setUser(null);
    clearOfflineSentryQueue();
  }

  /**
   * Drop any offline-queued Sentry envelopes that carry auth-bearing breadcrumbs.
   * deleteDatabase fires `blocked` while the transport holds its connection open,
   * so open a second connection and clear the object store directly.
   *
   * Two traps:
   * - A versionless open() _creates_ the database if it does not exist yet, as a
   *   store-less v1 — after which Sentry's own lazy upgrade never runs
   *   (createObjectStore('queue') only happens inside Sentry's onupgradeneeded),
   *   leaving the queue permanently broken for the profile. Aborting the upgrade
   *   transaction avoids ever creating it ourselves.
   * - The whole thing only makes sense if Sentry was initialized; in dev/test
   *   without a client there is no queue to clear and nothing should touch IndexedDB.
   */
  function clearOfflineSentryQueue() {
    if (!Sentry.getClient()) return;
    try {
      const req = indexedDB.open('sentry-offline');
      // Only an existing queue needs clearing; abort so a missing DB is not
      // created as a store-less v1 that silently breaks Sentry's offline queue.
      req.onupgradeneeded = () => req.transaction?.abort();
      // The abort above rejects the open request; consume the error so it
      // does not surface as an unhandled error event.
      req.onerror = (event) => {
        event.preventDefault();
      };
      req.onsuccess = () => {
        const db = req.result;
        try {
          const tx = db.transaction('queue', 'readwrite');
          tx.objectStore('queue').clear();
          tx.oncomplete = () => db.close();
        } catch {
          db.close();
        }
      };
    } catch {
      // indexedDB unavailable (Firefox strict cookie blocking, happy-dom, etc.)
    }
  }

  /**
   * Clear out (a presumably expired) authentication token, so that the 2fa screen will be shown
   */
  function clearAuth() {
    if (oidcToken.value) {
      // Remove origin, because we're going to use this to client route
      const currentOidcFrom = location.href.replace(location.origin, '');
      // Prevent OIDC login loop
      if (!currentOidcFrom.startsWith('/auth/login/org-')) {
        oidcFrom.set(currentOidcFrom);
      }
    }
    // Cancel out any in-flight queries and remove all queries from the cache, except for whoami
    // Note: don't use queryClient.clear() here, because it can throw a CancelledError
    // We use matchQuery with anyCurrent() (prefix match) to exclude all whoami query variants,
    // regardless of token values — a reference equality check would always return true.
    void queryClient.cancelQueries({
      predicate: (query) => !matchQuery({ queryKey: userQueryKeys.anyCurrent() }, query),
    });
    queryClient.removeQueries({
      predicate: (query) => !matchQuery({ queryKey: userQueryKeys.anyCurrent() }, query),
    });
    // Remove the auth token, which will cause a redirect to an mfa page (login or registration) or oidc login
    authToken.clear();
    oidcToken.clear();
  }

  /**
   * This private function clears out all existing authentication cookies and user
   * information. The query cache is cleared by the auth-state effect above as soon as
   * authState transitions to 'unauthenticated' — doing it here synchronously caused a
   * brief post-logout refetch wave because clear() un-suspended mounted queries before
   * the redirect committed.
   *
   * It does not clear the login url from localStorage, so we can show that option on the login page
   */
  function _clearBrowserAuthState() {
    _clearLocalAuthState();
  }

  // Return the user object and auth methods
  return {
    user,
    userToken: userToken.value,
    authToken: authToken.value,
    oidcToken: oidcToken.value,
    oidcLoginURL: oidcLogin.url,
    clearOIDCLoginURL: oidcLogin.clear,
    redirectToOIDC: oidcRedirect.shouldRedirect,
    sendMagicLink,
    verifyMagicLink,
    signout,
    registerMFA,
    registerWebAuthn,
    loginWithWebAuthn,
    loginWithMFA,
    getAuthState,
    clearOIDCFrom: oidcFrom.clear,
    startOIDC,
    startOIDCByEmail,
    verifyOIDC,
    hasUserRegisteredMFA,
    clearAuth,
    clearInvalidSession,
  };
}
